// =============================================================================
// Live updates without polling.
//
// The app used to ask the server for changes every 5 seconds. Supabase
// Realtime streams Postgres row changes over one WebSocket instead: we
// subscribe once per signed-in user to every table the app reads (RLS means
// we only ever hear about rows we may see) and run the existing silent reload
// when something arrives, debounced so a burst of edits costs one reload.
//
// Polling stays as a slow safety net (see App.js): every 60 s while the
// channel is healthy, every 5 s if it is not — so a broken WebSocket degrades
// to the old behaviour instead of a stale app.
//
// Server side: the tables must be in the `supabase_realtime` publication —
// supabase/realtime.sql.
// =============================================================================

import { supabase } from './supabase';

export const REALTIME_TABLES = [
  'calendar_events', 'calendar_birthdays', 'calendar_categories', 'calendar_settings', 'calendar_deletions',
  'shared_calendars', 'shared_calendar_members', 'shared_calendar_categories', 'shared_calendar_birthdays',
  'todo_tasks', 'todo_groups',
  'shopping_items', 'shopping_recipes', 'shared_shopping_lists', 'shared_shopping_list_members',
  'timetracker_entries', 'timetracker_companies',
  'budget_income', 'budget_expenses', 'budget_savings',
  'budget_expense_categories', 'budget_income_categories', 'budget_savings_categories', 'budget_savings_accounts',
  'fitness_sports', 'fitness_personal_bests', 'fitness_workouts', 'fitness_settings',
  'diary_entries', 'projects', 'tutoring_students', 'tutoring_sessions', 'tutoring_payments',
  'fuel_logs', 'fuel_entries', 'fuel_log_members', 'fuel_settlements',
  'user_settings'
];

// Subscribe to row changes for `userId`. `onChange` fires at most once per
// `debounceMs` no matter how many rows changed. Returns { stop, isLive }.
export const subscribeToChanges = (userId, onChange, { debounceMs = 400, client = supabase } = {}) => {
  if (!userId || !client?.channel) return { stop: () => {}, isLive: () => false };
  let status = 'connecting';
  let timer = null;
  const fire = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      onChange();
    }, debounceMs);
  };
  let channel = client.channel(`eulea-${userId}`);
  for (const table of REALTIME_TABLES) {
    channel = channel.on('postgres_changes', { event: '*', schema: 'public', table }, fire);
  }
  channel.subscribe((next) => {
    status = next;
  });
  return {
    stop: () => {
      if (timer) clearTimeout(timer);
      client.removeChannel(channel);
    },
    isLive: () => status === 'SUBSCRIBED'
  };
};
